'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import UserProfileDropdown from '@/components/common/UserProfileDropdown';
import NotificationPanel from '@/components/common/NotificationPanel';
import { useAuth } from '@/contexts/AuthContext';
import { TIER_INFO, OrganizationTier } from '@/lib/featureGates';
import AppLogo from '@/components/ui/AppLogo';
import { useTheme } from '@/contexts/ThemeContext';
import { trackThemeToggle } from '@/lib/analytics/googleAnalytics';
import KeyboardShortcutsModal from '@/components/common/KeyboardShortcutsModal';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useRouter } from 'next/navigation';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const pathname = usePathname();
  const { user, currentOrganization, currentMembership } = useAuth();
  const { resolvedTheme, toggleTheme } = useTheme();
  const router = useRouter();

  // Role helpers
  const isAdminUser =
    currentMembership?.role === 'owner' || currentMembership?.role === 'admin';

  // Customer-facing navigation groups
  const navGroups = [
    {
      id: 'governance',
      label: '🏛️ Governance',
      items: [
        { href: '/executive-dashboard', label: 'Dashboard' },
        { href: '/ai-use-case-management', label: 'AI Use Cases' },
        { href: '/risk-assessment-interface', label: 'Risk Assessment' },
        { href: '/impact-assessment-interface', label: 'Impact Assessment' },
        { href: '/controls-management', label: 'Controls' },
        { href: '/certification-management-center', label: 'Certifications' },
        { href: '/board-report', label: 'Board Report' },
        { href: '/audit-log-interface', label: 'Audit Logs' },
      ],
    },
    {
      id: 'ai-tools',
      label: '🤖 AI Tools',
      items: [
        { href: '/ai-policy-generator', label: 'AI Policies' },
        { href: '/governed-ai-workspace', label: 'AI Workspace' },
        { href: '/ai-output-library', label: 'Output Library' },
        { href: '/ai-document-analyzer', label: 'Document Analyzer' },
        { href: '/ai-compliance', label: 'AI Compliance' },
        { href: '/ai-approval-queue', label: 'Approvals' },
        { href: '/natural-language-query-engine', label: 'NL Query Engine' },
      ],
    },
    {
      id: 'knowledge',
      label: '📚 Resources',
      items: [
        { href: '/searchable-resource-hub', label: 'Resource Hub' },
        { href: '/templates-library', label: 'Templates Library' },
        { href: '/case-studies', label: 'Case Studies' },
        { href: '/compliance-cost-roi-report', label: 'ROI Report' },
      ],
    },
    {
      id: 'operations',
      label: '⚙️ Operations',
      items: [
        { href: '/unified-export-center', label: 'Exports' },
        { href: '/import-center', label: 'Import Center' },
        { href: '/team-members-management', label: 'Team Members' },
        { href: '/sso-configuration-center', label: 'SSO Config' },
        { href: '/integrations-hub', label: 'Integrations' },
        { href: '/workflow-automation-center', label: 'Workflows' },
        { href: '/usage-analytics', label: 'Usage & Analytics' },
      ],
    },
    {
      id: 'support',
      label: '💬 Support',
      items: [
        { href: '/support', label: 'Help & Support' },
        { href: '/partner-referral-program-dashboard', label: 'Partner Program' },
        { href: '/chrono-mind-certification-program', label: 'Certification Program' },
      ],
    },
  ];

  // Compliance dropdown — visible to owner/admin/compliance_officer
  const complianceNavItems = [
    { href: '/iso42001-wizard', label: '📜 ISO 42001 Wizard', badge: 'Enterprise' },
    { href: '/eu-ai-act-wizard', label: '🇪🇺 EU AI Act Wizard', badge: 'Pro+' },
    { href: '/soc2-mapping', label: '📊 SOC 2 Mapping', badge: null },
    { href: '/gdpr-compliance-center', label: '🇪🇺 GDPR Compliance', badge: 'Pro+' },
    { href: '/certification-management-center', label: '🏅 Certifications', badge: null },
    { href: '/chrono-mind-certification-program', label: '🎓 Certification Program', badge: 'New' },
    { href: '/ai-governance-maturity-certificate', label: '🏆 Maturity Certificate', badge: 'New' },
    { href: '/import-center', label: '📥 Import Center', badge: null },
    { href: '/custom-framework-builder', label: '🏗️ Framework Builder', badge: 'Custom' },
    { href: '/audit-firm-portal', label: '🔍 Audit Firm Portal', badge: 'Admin' },
    { href: '/advanced-role-customization', label: '🛡️ Role Management', badge: null },
  ];

  const isActivePath = (path: string) => pathname === path;
  const isCompliancePath = complianceNavItems.some(item => pathname === item.href);
  const isGroupActive = (group: typeof navGroups[0]) =>
    group.items.some(item => pathname === item.href);

  const toggleDropdown = (id: string) => {
    setOpenDropdown(prev => (prev === id ? null : id));
  };
  const closeDropdown = () => setOpenDropdown(null);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleThemeToggle = () => {
    const fromTheme = resolvedTheme as 'light' | 'dark';
    const toTheme = fromTheme === 'light' ? 'dark' : 'light';
    trackThemeToggle(fromTheme, toTheme);
    toggleTheme();
  };

  const handleOpenShortcuts = useCallback(() => {
    setIsShortcutsModalOpen(true);
  }, []);

  const handleCloseShortcuts = useCallback(() => {
    setIsShortcutsModalOpen(false);
  }, []);

  useKeyboardShortcuts({
    onHelp: handleOpenShortcuts,
    onEscape: () => {
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
      if (isShortcutsModalOpen) setIsShortcutsModalOpen(false);
      if (openDropdown) setOpenDropdown(null);
    },
    onNewUseCase: () => router.push('/ai-use-case-management'),
  });

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[1000] header-premium shadow-elevation-2 transition-smooth">
        <nav className="mx-auto max-w-[1400px] px-6" aria-label="Main navigation">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <Link
                href={user ? '/executive-dashboard' : '/'}
                className="flex items-center transition-smooth hover:opacity-80 focus:outline-none focus-gold rounded-md"
                aria-label="ChronoMind AI - Go to home"
              >
                <AppLogo
                  src="/assets/images/Untitled_design-27-1772027045051.png"
                  size={52}
                  className="mr-3"
                />
                <span className="font-heading text-xl font-bold text-foreground">
                  ChronoMind™ AI
                </span>
              </Link>
            </div>

            {/* Desktop Nav — only shown when authenticated */}
            {user && (
              <nav className="hidden items-center space-x-1 md:flex" aria-label="Primary navigation">
                {navGroups.map(group => (
                  <div key={group.id} className="relative">
                    <button
                      onClick={() => toggleDropdown(group.id)}
                      onBlur={() => setTimeout(closeDropdown, 150)}
                      className={`relative flex items-center gap-1 px-3 py-2 text-sm font-medium transition-all duration-200 rounded-md focus:outline-none focus-gold ${
                        isGroupActive(group)
                          ? 'text-accent font-semibold' :'text-foreground hover:text-accent hover:bg-accent/5'
                      }`}
                    >
                      {group.label}
                      <svg
                        className={`h-3 w-3 transition-transform ${
                          openDropdown === group.id ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      {isGroupActive(group) && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent rounded-full" />
                      )}
                    </button>

                    {openDropdown === group.id && (
                      <div className="absolute top-full left-0 z-50 mt-1 w-52 rounded-xl border border-white/10 bg-[#0F172A] shadow-2xl py-1">
                        {group.items.map(item => (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={closeDropdown}
                            className={`flex items-center px-4 py-2.5 text-sm transition-colors ${
                              isActivePath(item.href)
                                ? 'bg-[#C9A961]/10 text-[#C9A961] font-medium'
                                : 'text-gray-300 hover:bg-white/5 hover:text-white'
                            }`}
                          >
                            {item.label}
                            {isActivePath(item.href) && (
                              <span className="ml-auto text-[#C9A961] text-xs">●</span>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Compliance Dropdown — admin/owner/compliance_officer only */}
                {(currentMembership?.role === 'owner' ||
                  currentMembership?.role === 'admin' ||
                  currentMembership?.role === 'compliance_officer') && (
                  <div className="relative">
                    <button
                      onClick={() => toggleDropdown('compliance')}
                      onBlur={() => setTimeout(closeDropdown, 150)}
                      className={`relative flex items-center gap-1 px-3 py-2 text-sm font-medium transition-all duration-200 rounded-md focus:outline-none focus-gold ${
                        isCompliancePath
                          ? 'text-accent font-semibold' :'text-foreground hover:text-accent hover:bg-accent/5'
                      }`}
                    >
                      📋 Compliance
                      <svg
                        className={`h-3 w-3 transition-transform ${
                          openDropdown === 'compliance' ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      {isCompliancePath && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent rounded-full" />
                      )}
                    </button>

                    {openDropdown === 'compliance' && (
                      <div className="absolute top-full left-0 z-50 mt-1 w-56 rounded-xl border border-white/10 bg-[#0F172A] shadow-2xl py-1">
                        {complianceNavItems.map(item => (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={closeDropdown}
                            className={`flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                              isActivePath(item.href)
                                ? 'bg-[#C9A961]/10 text-[#C9A961]'
                                : 'text-gray-300 hover:bg-white/5 hover:text-white'
                            }`}
                          >
                            <span>{item.label}</span>
                            {item.badge && (
                              <span className="rounded-full bg-[#C9A961]/20 px-1.5 py-0.5 text-[9px] font-bold text-[#C9A961]">
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </nav>
            )}

            {/* Admin-only nav items: Tutorial, Launch, Deployment Checklist, API Docs */}
            {user && isAdminUser && (
              <div className="relative hidden md:block">
                <button
                  onClick={() => toggleDropdown('admin-tools')}
                  onBlur={() => setTimeout(closeDropdown, 150)}
                  className={`relative flex items-center gap-1 px-3 py-2 text-sm font-medium transition-all duration-200 rounded-md focus:outline-none focus-gold ${
                    ['/onboarding-tutorial', '/launch-readiness', '/deployment-checklist', '/api-reference-documentation'].includes(pathname)
                      ? 'text-accent font-semibold' :'text-foreground hover:text-accent hover:bg-accent/5'
                  }`}
                >
                  🔧 Admin Tools
                  <svg
                    className={`h-3 w-3 transition-transform ${
                      openDropdown === 'admin-tools' ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openDropdown === 'admin-tools' && (
                  <div className="absolute top-full left-0 z-50 mt-1 w-52 rounded-xl border border-white/10 bg-[#0F172A] shadow-2xl py-1">
                    {[
                      { href: '/onboarding-tutorial', label: '📖 Tutorial' },
                      { href: '/launch-readiness', label: '🚀 Launch' },
                      { href: '/deployment-checklist', label: '✅ Deployment Checklist' },
                      { href: '/api-reference-documentation', label: '📄 API Docs' },
                      { href: '/admin-console', label: '⚙️ Admin Console' },
                      { href: '/admin-verification', label: '🔍 Verification' },
                      { href: '/setup-progress', label: '⚙️ Setup Progress' },
                    ].map(item => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={closeDropdown}
                        className={`flex items-center px-4 py-2.5 text-sm transition-colors ${
                          isActivePath(item.href)
                            ? 'bg-[#C9A961]/10 text-[#C9A961] font-medium'
                            : 'text-gray-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        {item.label}
                        {isActivePath(item.href) && (
                          <span className="ml-auto text-[#C9A961] text-xs">●</span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Right side controls */}
            <div className="flex items-center space-x-3">
              {currentOrganization?.organizationTier && (
                <div
                  className={`hidden rounded-full px-3 py-1 font-body text-xs font-semibold md:block border border-gold ${
                    TIER_INFO[currentOrganization.organizationTier as OrganizationTier].bgColor
                  } ${
                    TIER_INFO[currentOrganization.organizationTier as OrganizationTier].color
                  }`}
                  title={
                    TIER_INFO[currentOrganization.organizationTier as OrganizationTier].description
                  }
                  aria-label={`Current tier: ${
                    TIER_INFO[currentOrganization.organizationTier as OrganizationTier].name
                  }`}
                >
                  {TIER_INFO[currentOrganization.organizationTier as OrganizationTier].name}
                </div>
              )}

              <button
                onClick={handleOpenShortcuts}
                aria-label="Keyboard shortcuts (press ? to open)"
                title="Keyboard shortcuts (?)"
                className="hidden md:flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-all duration-200 hover:border-accent/40 hover:text-accent hover:bg-accent/5 focus:outline-none focus-gold"
              >
                <span className="font-mono text-xs font-bold">?</span>
              </button>

              <NotificationPanel />
              <button
                onClick={handleThemeToggle}
                aria-label={
                  resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
                }
                className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-foreground transition-all duration-300 hover:border-accent/40 hover:bg-accent/5 hover:scale-105 focus:outline-none focus-gold"
              >
                {resolvedTheme === 'dark' ? (
                  <Icon name="SunIcon" size={18} className="text-accent" />
                ) : (
                  <Icon name="MoonIcon" size={18} className="text-muted-foreground" />
                )}
              </button>
              <UserProfileDropdown />

              <button
                onClick={toggleMobileMenu}
                className="rounded-md p-2 text-foreground transition-smooth hover:bg-accent/10 md:hidden focus:outline-none focus-gold"
                aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div
              id="mobile-menu"
              role="navigation"
              aria-label="Mobile navigation"
              className="fixed top-16 left-0 right-0 bottom-0 z-[999] overflow-y-auto border-t border-border py-4 md:hidden"
              style={{
                backgroundColor: '#0F172A',
                animation: 'slideDown 200ms ease-out forwards',
              }}
            >
              <div className="space-y-1 px-2">
                {user && navGroups.map(group => (
                  <div key={group.id}>
                    <div className="px-4 pt-4 pb-2">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        {group.label}
                      </p>
                    </div>
                    {group.items.map(item => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        aria-current={isActivePath(item.href) ? 'page' : undefined}
                        className={`flex items-center rounded-md px-4 py-3 font-body text-base font-medium transition-smooth focus:outline-none focus-gold ${
                          isActivePath(item.href)
                            ? 'bg-accent/10 text-accent border-l-2 border-accent' :'text-foreground hover:bg-accent/5 hover:text-accent'
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                ))}

                {/* Compliance section in mobile — admin/owner/compliance_officer only */}
                {user && (currentMembership?.role === 'owner' ||
                  currentMembership?.role === 'admin' ||
                  currentMembership?.role === 'compliance_officer') && (
                  <div>
                    <div className="px-4 pt-4 pb-2">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        📋 Compliance
                      </p>
                    </div>
                    {complianceNavItems.map(item => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center justify-between rounded-md px-4 py-3 font-body text-base font-medium transition-smooth focus:outline-none focus-gold ${
                          isActivePath(item.href)
                            ? 'bg-accent/10 text-accent border-l-2 border-accent' :'text-foreground hover:bg-accent/5 hover:text-accent'
                        }`}
                      >
                        <span>{item.label}</span>
                        {item.badge && (
                          <span className="rounded-full bg-accent/20 px-1.5 py-0.5 text-[9px] font-bold text-accent">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}

                {/* Admin Console link in mobile — owner/admin only */}
                {user && isAdminUser && (
                  <div>
                    <div className="px-4 pt-4 pb-2">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        🔧 Admin
                      </p>
                    </div>
                    <Link
                      href="/admin-console"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center rounded-md px-4 py-3 font-body text-base font-medium transition-smooth focus:outline-none focus-gold ${
                        isActivePath('/admin-console')
                          ? 'bg-accent/10 text-accent border-l-2 border-accent' :'text-foreground hover:bg-accent/5 hover:text-accent'
                      }`}
                    >
                      Admin Console
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      <KeyboardShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={handleCloseShortcuts}
      />
    </>
  );
};

export default Header;